import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminList, adminUpdate, adminDelete } from "@/lib/api";
import { CATEGORY_LABELS } from "@/lib/categories";
import { Check, X, Trash2, Lock } from "lucide-react";

const ADMIN_KEY = "sixclick_admin";
const PASSWORD = "sitrep-2026"; // simple gate (documented in About/README)

export default function Admin() {
    const [authed, setAuthed] = useState(false);
    const [pw, setPw] = useState("");
    const [tab, setTab] = useState("pending");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (localStorage.getItem(ADMIN_KEY) === "1") setAuthed(true);
    }, []);

    const load = () => {
        setLoading(true);
        adminList(tab === "all" ? undefined : tab)
            .then(setItems)
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => { if (authed) load(); /* eslint-disable-next-line */ }, [tab, authed]);

    const submit = (e) => {
        e.preventDefault();
        if (pw === PASSWORD) {
            localStorage.setItem(ADMIN_KEY, "1");
            setAuthed(true);
        } else {
            toast.error("Access denied.");
        }
    };

    const act = async (id, status) => {
        try {
            await adminUpdate(id, status);
            toast.success(`Marked ${status}`);
            load();
        } catch { toast.error("Action failed"); }
    };

    const del = async (id) => {
        if (!confirm("Delete permanently?")) return;
        try {
            await adminDelete(id);
            toast.success("Deleted");
            load();
        } catch { toast.error("Delete failed"); }
    };

    if (!authed) {
        return (
            <div className="max-w-md mx-auto px-4 py-24">
                <div className="tactical-card p-8 hud-brackets">
                    <Lock className="w-8 h-8 text-volt mb-3" />
                    <div className="mono-label mb-2">// RESTRICTED ACCESS</div>
                    <h1 className="font-oswald uppercase text-2xl mb-6">Command Center</h1>
                    <form onSubmit={submit} className="space-y-3">
                        <input
                            data-testid="admin-pw-input"
                            type="password"
                            value={pw}
                            onChange={(e) => setPw(e.target.value)}
                            placeholder="Access code"
                            className="w-full bg-[#0e1319] border border-white/15 focus:border-volt outline-none px-3 py-2 text-sm text-white"
                        />
                        <button data-testid="admin-login-btn" type="submit" className="btn-volt w-full justify-center">
                            Authenticate
                        </button>
                    </form>
                    <div className="mono-label text-[9px] text-[#64748b] mt-4">
                        Demo code: sitrep-2026
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
            <div className="mono-label text-volt mb-2">// COMMAND CENTER</div>
            <h1 className="font-oswald uppercase tracking-wide text-3xl md:text-4xl mb-6">
                Discount Review
            </h1>

            <div className="flex gap-2 mb-4">
                {[
                    ["pending", "Pending"],
                    ["approved", "Approved"],
                    ["rejected", "Rejected"],
                    ["all", "All"],
                ].map(([k, l]) => (
                    <button
                        key={k}
                        data-testid={`admin-tab-${k}`}
                        onClick={() => setTab(k)}
                        className={`px-3 py-2 font-oswald uppercase tracking-wider text-xs border ${
                            tab === k ? "bg-volt text-black border-volt" : "border-white/15 text-[#94a3b8]"
                        }`}
                    >
                        {l}
                    </button>
                ))}
            </div>

            <div className="mono-label mb-3">
                {loading ? "LOADING..." : `${items.length} RECORDS`}
            </div>

            <div className="space-y-3">
                {items.map((d) => (
                    <div key={d.id} data-testid={`admin-row-${d.id}`} className="tactical-card p-4 flex flex-col md:flex-row gap-4">
                        <div className="flex-1">
                            <div className="mono-label text-[10px] mb-1">
                                {CATEGORY_LABELS[d.category]} · {d.city}, {d.state} · {d.status.toUpperCase()} · {d.source}
                            </div>
                            <div className="font-oswald text-lg uppercase">{d.title}</div>
                            <div className="text-sm text-[#94a3b8]">{d.business_name}</div>
                            <div className="text-xs text-[#94a3b8] mt-1">{d.discount_details}</div>
                            {d.submitted_by_email && (
                                <div className="mono-label text-[10px] mt-2">by {d.submitted_by_name || "—"} · {d.submitted_by_email}</div>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2 items-start">
                            {d.status !== "approved" && (
                                <button
                                    data-testid={`approve-${d.id}`}
                                    onClick={() => act(d.id, "approved")}
                                    className="px-3 py-2 bg-volt text-black font-oswald uppercase text-xs tracking-wider inline-flex items-center gap-1"
                                >
                                    <Check className="w-3 h-3" /> Approve
                                </button>
                            )}
                            {d.status !== "rejected" && (
                                <button
                                    data-testid={`reject-${d.id}`}
                                    onClick={() => act(d.id, "rejected")}
                                    className="px-3 py-2 border border-white/20 text-[#94a3b8] font-oswald uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:border-[#ff3366] hover:text-[#ff3366]"
                                >
                                    <X className="w-3 h-3" /> Reject
                                </button>
                            )}
                            <button
                                data-testid={`delete-${d.id}`}
                                onClick={() => del(d.id)}
                                className="px-3 py-2 border border-white/20 text-[#94a3b8] font-oswald uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:border-[#ff3366] hover:text-[#ff3366]"
                            >
                                <Trash2 className="w-3 h-3" /> Delete
                            </button>
                        </div>
                    </div>
                ))}
                {!loading && items.length === 0 && (
                    <div className="tactical-card p-8 text-center text-[#64748b]">No records in this queue.</div>
                )}
            </div>
        </div>
    );
}
